import type { GeneratedNpc, Playbook } from '@bid/shared-types';

export interface NpcGenerationOptions {
  playbook?: Playbook;
  name?: string;
  faction?: string;
}

export const generateNpc = (options: NpcGenerationOptions = {}): GeneratedNpc => {
  // TODO: Implement actual NPC generation logic
  const playbook = options.playbook || 'Cutter';

  const contacts = options.faction ? generateContacts(options.faction) : undefined;

  return {
    name: options.name || generateRandomName(),
    playbook,
    actionRatings: generateActionRatings(playbook),
    specialAbilities: generateSpecialAbilities(playbook),
    vice: generateVice(),
    vicePurveyor: generateVicePurveyor(),
    factionAffiliation: options.faction,
    contacts,
  };
};

const generateRandomName = (): string => {
  const firstNames = ['Rex', 'Lyra', 'Severus', 'Mila', 'Thorne', 'Iris'];
  const lastNames = ['Malone', 'Blackwood', 'Crow', 'Vane', 'Steel', 'Ash'];
  const nicknames = ['The Butcher', 'The Ghost', 'Ironhand', 'Silvertongue'];

  const first = firstNames[Math.floor(Math.random() * firstNames.length)];
  const last = lastNames[Math.floor(Math.random() * lastNames.length)];
  const nickname =
    Math.random() > 0.7 ? ` '${nicknames[Math.floor(Math.random() * nicknames.length)]}'` : '';

  return `${first}${nickname} ${last}`;
};

export const generateActionRatings = (playbook: Playbook): Record<string, number> => {
  // TODO: Implement playbook-specific action ratings
  const baseRatings: Record<string, number> = {
    skirmish: 0,
    command: 0,
    study: 0,
    survey: 0,
    tinker: 0,
    finesse: 0,
    prowl: 0,
    wreck: 0,
    attune: 0,
    consort: 0,
    sway: 0,
  };

  // Add playbook-specific bonuses
  const playbookBonuses: Record<Playbook, string[]> = {
    Cutter: ['skirmish', 'command'],
    Hound: ['hunt', 'survey'],
    Leech: ['tinker', 'wreck'],
    Lurk: ['prowl', 'finesse'],
    Slide: ['consort', 'sway'],
    Spider: ['consort', 'command'],
    Whisper: ['attune', 'study'],
  };

  const bonuses = playbookBonuses[playbook] || [];
  bonuses.forEach(action => {
    baseRatings[action] = Math.floor(Math.random() * 2) + 1;
  });

  return baseRatings;
};

export const generateSpecialAbilities = (playbook: Playbook): string[] => {
  // TODO: Implement playbook-specific abilities
  const playbookAbilities: Record<Playbook, string[]> = {
    Cutter: ['Bodyguard', 'Ghost Fighter', 'Leader'],
    Hound: ['Sharpshooter', 'Survivor', 'Tough as Nails'],
    Leech: ['Alchemist', 'Analyst', 'Artificer'],
    Lurk: ['Ambush', 'Daredevil', "The Devil's Footsteps"],
    Slide: ['A Little Something on the Side', 'Fake', 'Masquerade'],
    Spider: ['Foresight', 'Patient', 'Weaving the Web'],
    Whisper: ['Compel', 'Ghost Mind', 'Iron Will'],
  };

  const abilities = playbookAbilities[playbook] || [];
  return abilities.slice(0, Math.floor(Math.random() * 2) + 3);
};

const generateVice = (): string => {
  const vices = ['Faith', 'Gambling', 'Luxury', 'Obligation', 'Pleasure', 'Stupor', 'Weird'];
  return vices[Math.floor(Math.random() * vices.length)];
};

const generateVicePurveyor = (): string => {
  const purveyors = [
    'The Church of the Ecstasy of the Flesh',
    'The Gondolier',
    'Seventh Tower Consortium',
    'The Forgotten Gods',
    'The Dimmer Sisters',
  ];
  return purveyors[Math.floor(Math.random() * purveyors.length)];
};

export const generateContacts = (faction: string): string[] => {
  const contactNames = ['Rex', 'Lyra', 'Severus', 'Mila', 'Thorne', 'Iris'];
  const factionContacts: Record<string, string[]> = {
    'The Lampblacks': ['Basso', 'Glen', 'Noggs'],
    'The Red Sashes': ['Mylera', 'Jin', 'Ukan'],
    'The Gray Cloaks': ['Sullivan', 'Rowan', 'Kael'],
    'The Billhooks': ['Carpenter', 'Rourke', 'Dunn'],
    'The Hive': ['Queen Lyssa', 'Beekeeper', 'Honey'],
    'The Unseen': ['Whisper', 'Specter', 'Phantom'],
  };

  const specificContacts = factionContacts[faction] || [];
  const randomContacts = contactNames.slice(0, 2);
  return [...specificContacts, ...randomContacts];
};
